/* A campaign's two dates on the screen: the arithmetic the date fields need,
 * and the line beside Save campaign that says what a press will do.
 *
 * Dates are the lake's form, "2026-11-09", and compare correctly as strings.
 * Every rule about them is the engine's (api.create_campaign and
 * api.schedule_campaign, engine migration 256), which refuses what breaks one
 * with a sentence. This only fills a field and says what will happen.
 * The engine's plan is docs/plans/campaign-scheduling.md. */

export type StartMode = "now" | "on_date" | "not_yet";

/** A calendar day as the lake writes it. */
export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Today in the reader's own calendar. */
export const todayIso = (now: Date = new Date()) => isoDay(now);

/* A lake date as a Date in the reader's calendar. Built by hand, as
   format.ts's parseLakeDate does and for its reason: new Date("2026-11-09")
   is UTC midnight, the day before anywhere west of UTC. */
function localDay(iso: string, plus = 0): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, (d ?? 1) + plus);
}

/** A date moved by whole days. */
export const addDays = (iso: string, days: number) => isoDay(localDay(iso, days));

/** The enrichment date suggested for a calling date: the lead days before
 *  it, or today when that has passed (James, 2026-10-19). */
export function suggestedEnrichOn(callOn: string, leadDays: number, today: string): string {
  const back = addDays(callOn, -leadDays);
  return back > today ? back : today;
}

/** A date as the engine writes it in its sentences, "Nov 9". */
export function dayWord(iso: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso)
    ? localDay(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : iso;
}

/** Where a new campaign goes in a sequence, for the line beside Save
 *  campaign: after or before `target`, in `owner`'s sequence, with `ahead`
 *  the campaign that will be ahead of it and whether that one is being
 *  called (engine migration 314). */
export type SequencePlace = {
  where: "after" | "before"; target: string; owner: string | null; ahead: string | null; aheadCalling: boolean;
};

/** The line beside Save campaign. */
export function saveLine(o: {
  anyEnrichment: boolean; mode: StartMode; enrichOn: string; callOn: string; today: string;
  sequence?: SequencePlace | null;
}): string {
  const parts: string[] = [];
  const s = o.sequence ?? null;
  if (s) parts.push(`It goes ${s.where} ${s.target}${s.owner ? ` in ${s.owner}'s sequence` : ""}.`);
  // A campaign in Planning behind one being called starts its enrichment
  // when it gets there, and one behind another starts when that one starts
  // calling (engine migrations 311 and 312).
  if (!o.anyEnrichment) parts.push("No enrichment. The list is ready to call when you save.");
  else if (s?.ahead && s.aheadCalling) parts.push(`Enrichment starts when you save, because ${s.ahead} is being called.`);
  else if (o.mode === "not_yet" && s?.ahead) parts.push(`Saved in Planning. Enrichment starts when ${s.ahead} starts calling.`);
  else if (o.mode === "not_yet") parts.push("Saved in Planning. Start enrichment from the campaign page.");
  else if (o.mode === "on_date" && o.enrichOn > o.today && s?.ahead) {
    parts.push(`Enrichment starts ${dayWord(o.enrichOn)}, or when ${s.ahead} starts calling if that is sooner.`);
  } else if (o.mode === "on_date" && o.enrichOn > o.today) parts.push(`Enrichment starts ${dayWord(o.enrichOn)}.`);
  else parts.push("Enrichment starts when you save.");
  // The calling date is the estimated start (James, 2026-11-02).
  if (o.callOn) parts.push(o.callOn === o.today ? "Estimated start today." : `Estimated start ${dayWord(o.callOn)}.`);
  return parts.join(" ");
}
